import { isEmpty } from 'lodash';
import { IChangePropertyPayload } from '@dash/editForm';
import { createReducer } from '../../../../../../helpers/reducer';
import { t } from '../../../../../../i18n';
import actionsTypes from './actionTypes';
import {
  Attribute,
  IPaymentDetailsState,
  ILoadFormDataSuccessPayload,
  ILoadPaymentSettingsSuccessPayload,
  IFieldTypes,
} from './types';

function validate(attr: Attribute, value: IFieldTypes) {
  let error = '';

  if (attr === 'enabled_payment_methods') {
    if (isEmpty(value)) {
      error = t('dash.admin.automationConfig.payment_methods.enabled_payment_methods_required_error_message');
    }
  }

  return error;
}

const getInitialField = (fieldName: Attribute, value: string | null = null) => ({
  value,
  error: validate(fieldName, value),
  name: fieldName,
  touched: false,
});

const initState: IPaymentDetailsState = {
  editForm: {
    enabled_payment_methods: getInitialField('enabled_payment_methods', null),
    default_payment_method: getInitialField('default_payment_method', null),
    approval: getInitialField('approval'),
    invoices_by_location: getInitialField('invoices_by_location'),
    rollup: getInitialField('rollup'),
    credit_days: getInitialField('credit_days'),
  },
  formInfo: undefined,
  paymentSettings: undefined,
  isLoading: false,
  isFormInfoLoading: false,
  isPaymentSettingsLoading: false,
  currentId: undefined,
};

function onLoadFormInfoRequest(state: IPaymentDetailsState) {
  return {
    ...state,
    isFormInfoLoading: true,
  };
}

function onLoadFormInfoSuccess(state: IPaymentDetailsState, { payload }: { payload: ILoadFormDataSuccessPayload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: data,
    isFormInfoLoading: false,
  };
}

function onLoadFormInfoError(state: IPaymentDetailsState) {
  return {
    ...state,
    isFormInfoLoading: false,
  };
}

function onLoadPaymentSettingsRequest(state: IPaymentDetailsState) {
  return {
    ...state,
    isPaymentSettingsLoading: true,
  };
}

function onLoadPaymentSettingsSuccess(
  state: IPaymentDetailsState,
  { payload }: { payload: ILoadPaymentSettingsSuccessPayload },
) {
  const { editForm } = state;
  const { data, id } = payload;
  const { default_payment_method, enabled_payment_methods, invoice_settings } = data;

  const { approval, credit_days, invoices_by_location, rollup } = invoice_settings;

  editForm.default_payment_method = {
    name: 'default_payment_method',
    value: default_payment_method.id,
    error: validate('default_payment_method', default_payment_method.id),
    touched: false,
  };

  editForm.enabled_payment_methods = {
    name: 'enabled_payment_methods',
    value: enabled_payment_methods.map(enabled_payment_method => enabled_payment_method.id),
    error: validate(
      'enabled_payment_methods',
      enabled_payment_methods.map(enabled_payment_method => enabled_payment_method.id),
    ),
    touched: false,
  };

  editForm.approval = {
    name: 'approval',
    value: approval.id,
    error: validate('approval', approval.id),
    touched: false,
  };

  editForm.credit_days = {
    name: 'approval',
    value: credit_days,
    error: validate('credit_days', credit_days),
    touched: false,
  };

  editForm.invoices_by_location = {
    name: 'invoices_by_location',
    value: invoices_by_location,
    error: validate('invoices_by_location', invoices_by_location),
    touched: false,
  };

  editForm.rollup = {
    name: 'rollup',
    value: rollup.id,
    error: validate('rollup', rollup.id),
    touched: false,
  };

  return {
    ...state,
    paymentSettings: data,
    isPaymentSettingsLoading: false,
    currentId: id,
  };
}

function onLoadPaymentSettingsError(state: IPaymentDetailsState) {
  return {
    ...state,
    isPaymentSettingsLoading: false,
  };
}

function onChangeProperty(
  state: IPaymentDetailsState,
  { payload }: { payload: IChangePropertyPayload<Attribute, IFieldTypes> },
) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onUpdatePaymentDetailsRequest(state: IPaymentDetailsState) {
  return {
    ...state,
    isLoading: true,
  };
}

function onUpdatePaymentDetailsSuccess(state: IPaymentDetailsState) {
  return {
    ...state,
    isLoading: false,
  };
}

function onUpdatePaymentDetailsError(state: IPaymentDetailsState) {
  return {
    ...state,
    isLoading: false,
  };
}

export default createReducer(initState, {
  [actionsTypes.LOAD_FORM_INFO_REQUEST]: onLoadFormInfoRequest,
  [actionsTypes.LOAD_FORM_INFO_SUCCESS]: onLoadFormInfoSuccess,
  [actionsTypes.LOAD_FORM_INFO_ERROR]: onLoadFormInfoError,
  [actionsTypes.LOAD_PAYMENT_SETTINGS_REQUEST]: onLoadPaymentSettingsRequest,
  [actionsTypes.LOAD_PAYMENT_SETTINGS_SUCCESS]: onLoadPaymentSettingsSuccess,
  [actionsTypes.LOAD_PAYMENT_SETTINGS_ERROR]: onLoadPaymentSettingsError,
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.UPDATE_PAYMENT_DETAILS_REQUEST]: onUpdatePaymentDetailsRequest,
  [actionsTypes.UPDATE_PAYMENT_DETAILS_SUCCESS]: onUpdatePaymentDetailsSuccess,
  [actionsTypes.UPDATE_PAYMENT_DETAILS_ERROR]: onUpdatePaymentDetailsError,
});
